/** @jsx jsx */
import React, { useCallback, useMemo, useState, type ComponentType } from 'react';
import { css, jsx } from '@compiled/react';
import throttle from 'lodash/throttle';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import type {
	AdditionalCreateIssueContext,
	InlineCreateStyles,
	CreateIssueReturn,
} from '@atlassian/jira-business-issue-create/src/types.tsx';
import InlineCreate from '@atlassian/jira-business-issue-create/src/ui/index.tsx';
import { ROW_APPEARANCE } from '@atlassian/jira-business-issue-create/src/ui/inline-create-form/index.tsx';
import type { ExpandedItems } from '@atlassian/jira-business-list-handlers/src/controllers/children/types.tsx';
import { useResizeObserver } from '@atlassian/jira-business-resize-observer/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { TimelineAddItemNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/jwm-timeline-nudge-tour/timeline-add-item/async.tsx';
import { zIndex } from '@atlassian/timeline-table/common/constants';
import { useHeaderState } from '@atlassian/timeline-table/common/context/header';
import type { Item } from '@atlassian/timeline-table/common/types/item';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fieldIds } from '../../../common/constants.tsx';
import { getTimelineTableItemsHeight } from '../../../common/utils/get-items-height/index.tsx';
import { useCreateItemAnchor } from '../../../controllers/create-item-anchor/index.tsx';
import { useOptimisticIssueCreate } from '../../../controllers/use-optimistic-issue-create/index.tsx';
import type { CreatedTimelineItemFragment as Issue } from '../../../services/create-issue/__generated_apollo__/CreatedTimelineItemFragment.ts';
import type { TimelineViewIssue } from '../../../services/get-timeline-view-issues/types.tsx';
import { getIssueAfter } from './utils.tsx';

const inlineCreateStyles = (height: number): InlineCreateStyles => ({
	horizontalPadding: `${token('space.100', '8px')} 0 ${token('space.150', '12px')}`,
	/* -1 to fit border-bottom */
	minHeight: `${height - 1}px`,
});

const inlineCreateStylesOld = (height: number): InlineCreateStyles => ({
	horizontalPadding: `${token('space.100', '8px')} 0 ${token('space.250', '20px')}`,
	/* -1 to fit border-bottom */
	minHeight: `${height - 1}px`,
	borderRadius: `0 0 0 ${token('border.radius.200', '8px')}`,
});

type Props = {
	issuesMap: Map<string, TimelineViewIssue>;
	loading: boolean;
	listWidth: number;
	height: number;
	items: Item[];
	expandedItems: ExpandedItems;
	timelineRef?: HTMLElement | null;
};

// Sticky inline create component that is positioned after the last issue,
// or sticks to the bottom of the viewport if issues go beyond the screen
export const StickyInlineCreate: ComponentType<Props> = ({
	issuesMap,
	loading,
	listWidth,
	height,
	items,
	expandedItems,
	timelineRef,
}: Props) => {
	const [{ createItemAnchor }] = useCreateItemAnchor();
	const [{ headerHeight }] = useHeaderState();
	const [containerHeight, setContainerHeight] = useState(
		() => (timelineRef?.offsetHeight || 0) - headerHeight,
	);

	const { addOptimisticIssue, updateIssue, removeOptimisticIssue } = useOptimisticIssueCreate();

	const isEmpty = items.length === 0;

	const onPrepareIssueContext = useCallback((): AdditionalCreateIssueContext => {
		// New issue will be created after the last visible issue in the viewport based on scroll position
		const issue = getIssueAfter(
			containerHeight,
			expandedItems,
			height,
			issuesMap,
			items,
			timelineRef,
		);

		if (!issue || !issue.rank) {
			return {};
		}

		return {
			rankAfter: {
				issueId: issue.issueId,
				rank: issue.rank,
			},
		};
	}, [containerHeight, expandedItems, height, issuesMap, items, timelineRef]);

	const { sticky, offsetTop } = useMemo(() => {
		const itemsHeight = getTimelineTableItemsHeight(items, expandedItems, height);
		// consider the height of the create item itself which is equal to 'height'
		const isSticky = itemsHeight + height > containerHeight;

		const createItemAnchorOffset = !isSticky && createItemAnchor ? height : 0;

		return {
			sticky: isSticky,
			offsetTop: itemsHeight + createItemAnchorOffset + headerHeight,
		};
	}, [containerHeight, createItemAnchor, height, items, expandedItems, headerHeight]);

	const throttledUpdateContainerHeight = useMemo(
		() =>
			throttle(() => {
				if (!timelineRef) {
					return;
				}

				setContainerHeight((timelineRef?.offsetHeight || 0) - headerHeight);
			}, 100),
		[headerHeight, timelineRef],
	);

	useResizeObserver(timelineRef ?? null, throttledUpdateContainerHeight);

	const onSubmit = useCallback(
		async ({ optimisticIssuePayload, promise }: CreateIssueReturn<Issue>) => {
			addOptimisticIssue(optimisticIssuePayload);

			try {
				const issue = await promise;
				if (issue) {
					updateIssue(issue);
				} else {
					removeOptimisticIssue(
						optimisticIssuePayload.issueId,
						optimisticIssuePayload.parentIssueId,
					);
				}
			} catch {
				removeOptimisticIssue(optimisticIssuePayload.issueId, optimisticIssuePayload.parentIssueId);
			}
		},
		[addOptimisticIssue, updateIssue, removeOptimisticIssue],
	);

	if (loading || !timelineRef) {
		return null;
	}

	return (
		<UFOSegment name="business-timeline-sticky-inline-create">
			<Wrapper width={listWidth} height={height} sticky={sticky} offsetTop={offsetTop}>
				<TimelineAddItemNudgeAsync attributes={{ isEmpty }}>
					<InlineCreate
						appearance={ROW_APPEARANCE}
						fieldIdsToReturn={fieldIds}
						inlineCreateStyles={
							isVisualRefreshEnabled()
								? {
										...inlineCreateStyles(height),
										...(fg('visual-refresh_drop_1')
											? {}
											: { borderRadius: `0 0 0 ${token('border.radius.200', '8px')}` }),
									}
								: inlineCreateStylesOld(height)
						}
						isCompact
						location="timelineFooter"
						onPrepareIssueContext={onPrepareIssueContext}
						onSubmit={onSubmit}
					/>
				</TimelineAddItemNudgeAsync>
			</Wrapper>
		</UFOSegment>
	);
};

const BORDER_OFFSET = 4;

interface WrapperProps {
	sticky: boolean;
	offsetTop: number;
	width: number;
	height: number;
	children: React.ReactNode;
}

const wrapperBaseStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	position: 'absolute',
	left: token('space.025', '2px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: zIndex.LIST_CREATE_ITEM,
	paddingTop: token('space.0', '0px'),
	paddingRight: token('space.0', '0px'),
	paddingBottom: token('space.0', '0px'),
	paddingLeft: token('space.0', '0px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderTop: `1px solid ${token('color.border.disabled', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `1px solid ${token('color.border.disabled', colors.N60)}`,
	borderRadius: '0px',
	display: 'flex',
	alignItems: 'center',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		width: '100%',
	},
});

const wrapperStickyStyles = css({
	bottom: token('space.negative.025', '-2px'),
});

const wrapperStickyStylesOld = css({
	borderRadius: `0 0 0 ${token('border.radius.200', '8px')}`,
	bottom: token('space.negative.025', '-2px'),
});

const Wrapper = ({ sticky, offsetTop, width, height, children, ...rest }: WrapperProps) => (
	<div
		css={[
			wrapperBaseStyles,
			// eslint-disable-next-line no-nested-ternary
			sticky
				? isVisualRefreshEnabled() && fg('visual-refresh_drop_1')
					? wrapperStickyStyles
					: wrapperStickyStylesOld
				: null,
		]}
		// eslint-disable-next-line jira/react/no-style-attribute
		style={{
			top: sticky ? 'auto' : `${offsetTop}px`,
			height: `${height - 1}px`,
			width: `${width - BORDER_OFFSET}px`,
		}}
		{...rest}
	>
		{children}
	</div>
);
